// delta-renderer.service.ts
import { Injectable } from '@angular/core';
import Quill, { Delta } from 'quill';

// Service to convert Quill Delta JSON to HTML
@Injectable({ providedIn: 'root' })
export class DeltaRendererService {
  private quill: Quill;

  constructor() {
    // Create a hidden off-screen container just for rendering
    const container = document.createElement('div');
    container.style.display = 'none';
    document.body.appendChild(container);
    this.quill = new Quill(container);
  }

  deltaToHtml(deltaJson: Delta): string {
    this.quill.setContents(deltaJson);
    return this.normalizeHtml(this.quill.root.innerHTML);
  }

  // Quill 2 renders every list (bullet and ordered alike) as an <ol> whose
  // <li> elements carry a `data-list` attribute, relying on the editor's
  // `.ql-editor`-scoped CSS to draw the correct marker. Outside the editor that
  // styling is absent, so bullet lists show as numbers. Rebuild the markup into
  // semantic <ul>/<ol> that render correctly with plain browser defaults.
  private normalizeHtml(html: string): string {
    const template = document.createElement('template');
    template.innerHTML = html;
    const root = template.content;

    root.querySelectorAll('ol, ul').forEach(list => this.rebuildList(list));

    // Remove Quill's marker/UI helper spans; native list markers replace them.
    root.querySelectorAll('.ql-ui').forEach(el => el.remove());

    return template.innerHTML;
  }

  private rebuildList(list: Element): void {
    const parent = list.parentNode;
    if (!parent) {
      return;
    }

    const items = Array.from(list.children).filter(child => child.tagName === 'LI');
    if (items.length === 0) {
      return;
    }

    // Split the flat Quill list into runs of consecutive items of the same
    // type, each wrapped in its own <ul> (bullet) or <ol> (everything else).
    const rebuilt: HTMLElement[] = [];
    let currentList: HTMLElement | null = null;
    let currentTag: string | null = null;

    for (const li of items) {
      const tag = li.getAttribute('data-list') === 'bullet' ? 'ul' : 'ol';
      if (tag !== currentTag) {
        currentList = document.createElement(tag);
        currentTag = tag;
        rebuilt.push(currentList);
      }
      li.removeAttribute('data-list');
      currentList!.appendChild(li);
    }

    rebuilt.forEach(newList => parent.insertBefore(newList, list));
    parent.removeChild(list);
  }

  htmlToDelta(html: string): Delta {
    const delta = this.quill.clipboard.convert({html: html});
  
    return delta;
  }
}